// Apps by SVT Consulting — one entry per app.
//
// An app with `site` links out to its own website (its privacy and support live there).
// An app with `privacy` / `support` / `terms` gets those pages hosted here at
// /apps/<slug>/privacy, /apps/<slug>/support and /apps/<slug>/terms — the URLs to give
// App Store Connect. Sections are { heading, paragraphs: [], bullets: [] }.

export const SUPPORT_EMAIL = 'support@svtconsulting.co';

export const apps = [
  {
    slug: 'studioflow',
    name: 'StudioFlow',
    tagline: 'Plan your week of classes, know the minute booking opens, keep your own record.',
    icon: '/images/apps/studioflow.png',
    platforms: ['iPhone', 'iPad'],
    status: 'coming-soon', // 'available' once the App Store URL exists
    storeUrl: null,
    accent: '#5B4B8A',
    description:
      'StudioFlow is a private planner for people who take group fitness classes at a studio ' +
      'where popular classes fill within minutes of opening. Set your weekly routine once; ' +
      'StudioFlow works out when each class opens for booking and reminds you the minute it ' +
      'does. You book in your studio’s own app, come back and tap Mark Booked. Afterwards, ' +
      'mark what you attended and StudioFlow keeps a clean monthly report. Everything stays on ' +
      'your phone — no account, no server, no analytics.',
    features: [
      { title: 'Booking-open reminders', text: 'A notification the minute each planned class opens for booking, plus an optional heads-up before and a “Still not booked?” after.' },
      { title: 'Your routine, your week', text: 'A weekly routine template builds your plan; Build My Week suggests a week from your goals and the timetable.' },
      { title: 'Planned, booked, attended', text: 'Three different facts, kept apart. StudioFlow never claims you attended a class unless you said so.' },
      { title: 'Monthly report', text: 'Minutes by week, class types, instructors and streaks — derived from what you marked, exportable as PDF or CSV.' },
      { title: 'Lifetime totals and milestones', text: 'Enter the classes you took before StudioFlow and keep your lifetime count and milestones going.' },
      { title: 'Friends (optional)', text: 'See which classes your friends are taking and let them see yours, so you can coordinate. Off until you turn it on.' },
    ],
    notAffiliated: 'StudioFlow is not affiliated with any fitness studio. It never books, cancels or logs in anywhere; booking happens in your studio’s own app or website.',
    privacy: {
      effective: 'October 29, 2026',
      intro:
        'StudioFlow is built so that we never have your data. This page explains, in plain language, ' +
        'what stays on your iPhone or iPad and the few things that leave it — only when you choose.',
      sections: [
        {
          heading: 'The short version',
          bullets: [
            'Local-first. Everything you enter is stored on your device, in the app’s own database, and nowhere else unless you choose otherwise.',
            'No account. There is no sign-up, and we run no server that holds your data.',
            'No analytics, no ads, no tracking. The app contains no analytics, advertising or crash-reporting code and does not track you across apps or websites.',
            'Data leaves your device only when you make it: exports and backups you create, a daily backup to your own iCloud Drive if you leave that on, calendar events if you turn that on, and the optional Friends feature.',
          ],
        },
        {
          heading: 'What stays on your device',
          paragraphs: [
            'Your routine, planned classes, bookings you marked, attendance marks, durations, notes, energy and difficulty ratings, goals and preferences, the class catalog and timetable you maintain, your studio’s booking rule, and your settings are all stored on your device. We have no copy and no way to see them.',
            'Reminders are local notifications scheduled on your device by iOS. Nothing is sent to us to make them fire.',
            'The classes you enter from your studio’s own achievements screen (“Classes before StudioFlow”) are stored on your device and count only toward your lifetime totals.',
          ],
        },
        {
          heading: 'When data leaves your device',
          paragraphs: ['Only in these cases, and only because you chose to:'],
          bullets: [
            'Exports and backups you create. PDF and CSV exports are plain, readable files; a backup is a single .sflow file. You decide where they go — Files, AirDrop, email or anywhere else — and once they leave the app, that destination’s privacy terms apply. We never receive them.',
            'iCloud Drive backup. If iCloud is available, StudioFlow writes one backup file a day into its own folder in your iCloud Drive and keeps the newest seven. This is your Apple iCloud storage, under your Apple Account; Apple’s iCloud terms apply and we cannot access it. You can turn it off in More → Your data.',
            'Calendar (optional). If you turn on calendar sync, classes you marked booked are added as events to a calendar StudioFlow creates on your device. Turning it off deletes that calendar.',
            'Opening your studio. Tapping Open Studio hands you to your studio’s own app or website. What happens there is governed by their privacy policy, not ours. StudioFlow sends them nothing.',
            'Friends (optional, off by default). See below.',
          ],
        },
        {
          heading: 'Friends',
          paragraphs: [
            'Friends lets you and people you invite see which classes each of you is taking. It is off until you turn it on, and turning it off removes everything you shared.',
            'When Friends is on, StudioFlow shares through Apple’s iCloud (CloudKit), under your own Apple Account, with only the friends you invite: your display name, your profile photo if you add one, and for each class in your plan its name, date, start time, studio and whether it is planned, booked or attended. Depending on the sharing level you choose per friend, this may be limited to your weekly streak and attended count, or paused entirely.',
            'Nothing else is shared: never your notes, ratings, durations, goals, blocked days, exports or backups. There is no server of ours in between — the data travels between your iCloud account and your friends’ iCloud accounts, and we cannot read it. Attendance is self-reported by each person.',
            'Removing a friend removes what you shared with them and what they shared with you, including cached photos. Friends does not work without an Apple Account signed in to iCloud.',
          ],
        },
        {
          heading: 'Notifications',
          paragraphs: [
            'All reminders are local notifications scheduled on your device. iOS asks for permission the first time you turn a reminder on. Booking-open reminders use the time-sensitive level so they can come through a Focus you allow it from; you can change that in iOS Settings. With Friends on, friend invitations and updates arrive through Apple’s push service as part of iCloud sharing; we operate no push server.',
          ],
        },
        {
          heading: 'What we never do',
          bullets: [
            'Collect, store or sell your data. We have no server, no database of users and no account system.',
            'Book, cancel, waitlist or log in to anything on your behalf.',
            'Use third-party SDKs for analytics, advertising or crash reporting.',
            'Read anything from your studio’s systems.',
          ],
        },
        {
          heading: 'Children',
          paragraphs: ['StudioFlow is not directed at children under 13 and we do not knowingly collect information from them. Since the app collects nothing, there is nothing for us to delete; deleting the app removes its data from the device.'],
        },
        {
          heading: 'Your choices',
          bullets: [
            'Everything is editable or reversible in the app. Erase all data (More → Your data) deletes the app’s database and cancels every reminder.',
            'Deleting the app from your device deletes all of its data on that device. Backups you placed in Files or iCloud Drive remain until you delete them.',
            'Turning Friends off removes what you shared.',
          ],
        },
        {
          heading: 'Changes to this policy',
          paragraphs: ['If StudioFlow ever changes how data is handled, this page will be updated and the effective date changed. Material changes will be noted in the app’s release notes.'],
        },
        {
          heading: 'Contact',
          paragraphs: ['SVT Consulting LLC, publisher of StudioFlow. Questions about this policy: ' + 'support@svtconsulting.co' + '.'],
        },
      ],
    },
    support: {
      intro: 'Answers to the common questions, and a real person at support@svtconsulting.co for everything else.',
      faq: [
        {
          q: 'I didn’t get the reminder when booking opened.',
          a: 'Check three things. In StudioFlow, More → Reminders: the Class booking reminders switch must be on — the “When booking opens” alert is always sent while it is. In iOS Settings → Notifications → StudioFlow: notifications must be allowed. If you use a Focus (Do Not Disturb, Sleep), allow time-sensitive notifications from StudioFlow, since booking-open alerts use that level. The class also needs a start time in your plan.',
        },
        {
          q: 'Does StudioFlow book the class for me?',
          a: 'No, and it never will. It tells you the minute booking opens and opens your studio’s own app or website, where you book. When you have, come back and tap Mark Booked so StudioFlow knows.',
        },
        {
          q: 'How does it know when booking opens?',
          a: 'From your studio’s booking rule, which you can see and edit in More → Booking rule (for example: 7 days before the class, 60 minutes after its start time). Every reminder shows how its time was worked out. The app asks you to re-check the rule every 90 days, because studios change them.',
        },
        {
          q: 'A class passed and it says “Not marked”.',
          a: 'StudioFlow never assumes you attended. A planned or booked class that passed without a mark stays “Not marked” until you tap Attended, Skipped or Not sure, from Home or the class itself. Marks can be edited any time.',
        },
        {
          q: 'How do I back up, or move to a new phone?',
          a: 'If iCloud is on, StudioFlow already keeps a daily backup in its own folder in your iCloud Drive (More → Your data shows the last one). On a new phone, onboarding offers to restore it. You can also make a backup file yourself (Back up now) and keep it in Files, and restore from any backup file under More → Your data.',
        },
        {
          q: 'How do Friends work, and what do they see?',
          a: 'Friends is off until you turn it on in More → Friends. You invite a friend with a link or code; they need StudioFlow and an Apple Account signed in to iCloud. When you accept each other you choose what to share: your classes (name, date, time, studio and planned/booked/attended), only your streaks, or nothing. Friends never see notes, ratings or durations. Remove a friend or turn Friends off at any time.',
        },
        {
          q: 'Can I add the classes I took before StudioFlow?',
          a: 'Yes. More → Your data → Classes before StudioFlow: enter the count per class type from your studio’s own app, with the date they were correct. They count toward your lifetime totals and milestones on Stats, never toward a month’s report.',
        },
        {
          q: 'How do I export my history?',
          a: 'Stats → Report has Export PDF (the month’s summary, charts and every class) and Export CSV (one row per class). More → Your data has Export all history (CSV). All open the share sheet so you can save to Files or send anywhere.',
        },
        {
          q: 'How do I delete everything?',
          a: 'More → Your data → Erase all data deletes the app’s database and cancels every reminder. It cannot be undone, so make a backup first if you might want it back. Deleting the app does the same for that device.',
        },
      ],
    },
    terms: {
      effective: 'October 29, 2026',
      sections: [
        { heading: 'What StudioFlow is', paragraphs: ['StudioFlow is a personal planning and record-keeping app published by SVT Consulting LLC. It helps you plan classes, reminds you when booking opens at your studio and keeps the record you enter. It is provided for personal use.'] },
        { heading: 'Not affiliated with any studio', paragraphs: ['StudioFlow is not affiliated with, endorsed by or connected to any fitness studio or gym. Class names and studio names in the app are the ones you use; they remain the property of their owners. StudioFlow never books, cancels, waitlists or logs in to anything on your behalf. Reservations, cancellations, fees and policies are entirely between you and your studio.'] },
        { heading: 'Reminders are reminders', paragraphs: ['Booking-open times are calculated from the booking rule you keep in the app. A reminder is not a reservation and not a guarantee that a class is available. Notifications depend on your device settings and iOS; we cannot guarantee delivery.'] },
        { heading: 'Your data', paragraphs: ['Everything you enter is yours and stays on your device, as described in the privacy policy. Attendance, minutes and reports are self-reported and derived from what you mark. You are responsible for keeping backups.'] },
        { heading: 'No warranty', paragraphs: ['StudioFlow is provided “as is”, without warranty of any kind. To the fullest extent permitted by law, SVT Consulting LLC is not liable for missed bookings, fees charged by a studio, lost data or any indirect or consequential loss arising from use of the app.'] },
        { heading: 'Changes', paragraphs: ['We may update the app and these terms. Continued use after an update means you accept the updated terms. Questions: support@svtconsulting.co.'] },
      ],
    },
  },
  {
    slug: 'field-pro',
    name: 'Field Pro',
    tagline: 'Field service management for AV, low-voltage and electrical contractors.',
    icon: '/images/apps/fieldpro.svg',
    platforms: ['iPhone', 'Android', 'Web'],
    status: 'coming-soon',
    site: 'https://fieldpro.cloud',
    accent: '#1F6FEB',
    description:
      'Field Pro is the operations platform for the trades the big players forgot: scheduling and dispatch, technician mobile app, quotes, invoices and job history for AV integrators and electrical contractors.',
  },
  {
    slug: 'riskscore360',
    name: 'RiskScore360',
    tagline: 'AI-assisted physical security risk assessment and remediation, for security consultants.',
    icon: null,
    platforms: ['iPhone', 'iPad', 'Web'],
    status: 'coming-soon',
    site: null,
    accent: '#0F3D5E',
    description:
      'An assessor walks a site with the offline-first field app \u2014 photos, voice notes and annotations against zones on a floor plan. AI proposes labels, findings and narrative; a qualified assessor accepts, edits or rejects every one. A versioned risk engine scores threat scenarios, Report Studio renders a white-labeled PDF, and clients track remediation in their portal. AI helps the assessor; it never decides whether a site is secure.',
  },
  {
    slug: 'armory360',
    name: 'Armory360',
    tagline: 'Privacy-first firearm, ammo and range log for iPhone and iPad.',
    icon: '/images/apps/armory360.png',
    platforms: ['iPhone', 'iPad'],
    status: 'coming-soon',
    site: 'https://armory360.app',
    accent: '#1A1A1A',
    description:
      'Tracks firearms, ammunition, range days, drills, maintenance and documents — all encrypted on-device behind PIN and Face ID, with no account, no analytics and optional end-to-end encrypted iCloud sync. Full details, support and privacy policy at armory360.app.',
  },
  {
    slug: 'ccb-nyc',
    name: 'CCB NYC',
    tagline: 'The community app for Cristo Centro de Bendici\u00f3n NYC.',
    icon: '/images/apps/ccb-nyc.png',
    platforms: ['iPhone', 'Android'],
    status: null,
    storeUrl: null,
    accent: '#0ea5e9',
    description:
      'CCB NYC keeps the congregation of Cristo Centro de Bendici\u00f3n NYC connected: announcements, ' +
      'service times, sermon videos, upcoming events, a verse of the day, prayer requests, a check-in ' +
      'for services, and giving through the church\u2019s Tithe.ly page. Built and maintained by SVT ' +
      'Consulting for the church.',
    features: [
      { title: 'Announcements and events', text: 'The latest news from the church and the events calendar, with Add to Calendar and share.' },
      { title: 'Watch', text: 'Sermon videos, opened in YouTube.' },
      { title: 'Prayer requests', text: 'Send a prayer request to the church\u2019s prayer team from the app.' },
      { title: 'Check in', text: 'Let the church know you were at a service, and whether it was your first time.' },
      { title: 'Verse of the day', text: 'A daily verse with its reference, easy to share.' },
      { title: 'Give', text: 'Opens the church\u2019s Tithe.ly giving page in your browser. The app never handles payment details.' },
    ],
    notAffiliated: null,
    privacy: {
      effective: 'October 29, 2026',
      intro:
        'CCB NYC is the mobile app of Cristo Centro de Bendici\u00f3n NYC (\u201cthe church\u201d). SVT Consulting LLC ' +
        'builds and maintains the app and its server on the church\u2019s behalf. This page explains what the ' +
        'app collects, why, and what you can do about it.',
      sections: [
        {
          heading: 'The short version',
          bullets: [
            'You can read announcements, events, sermons and the verse of the day without an account.',
            'An account (name, email, phone, password) is needed to keep a profile and see your check-in history. You can delete it from inside the app.',
            'Prayer requests and check-ins send what you type in the form to the church\u2019s own server, so the church can respond.',
            'Giving happens on Tithe.ly and videos play on YouTube \u2014 in your browser or their app, under their privacy policies. The app never sees payment details.',
            'No advertising, no ad tracking, no sale of your data.',
          ],
        },
        {
          heading: 'Information you give us',
          bullets: [
            'Account: first and last name, email address, phone number and a password when you register; you can edit these on My Profile. Passwords are stored hashed on the server, never in plain text.',
            'Prayer requests: your name, your email if you add it, and the text of your request.',
            'Check-in: your name, email, phone, which service you attended, whether it was your first time, and any note you add.',
            'Profile changes and password-reset requests you make.',
          ],
        },
        {
          heading: 'Information collected automatically',
          paragraphs: [
            'When the app talks to the church\u2019s server, the server receives the ordinary technical details of any internet request (your IP address, the time, and the app\u2019s request). The app contains no analytics, advertising or tracking SDKs. On your device, the app keeps your sign-in token and your display preferences so you stay signed in.',
            'The app does not use your location. It does not read your contacts, photos or calendar; \u201cAdd to Calendar\u201d hands the event to your calendar app only when you tap it.',
          ],
        },
        {
          heading: 'How the information is used',
          bullets: [
            'To run your account and show you your own check-in history.',
            'To pass prayer requests to the church\u2019s prayer team and to let the church follow up on a first-time visit if you asked for that.',
            'To keep attendance counts for the church\u2019s own planning.',
            'To send account emails such as a password reset.',
          ],
          paragraphs: ['We do not use it for advertising, we do not build profiles for marketing, and we do not sell or rent it.'],
        },
        {
          heading: 'Who sees it',
          paragraphs: [
            'Your information is stored on a server operated for the church by SVT Consulting LLC (hosted on Railway in the United States). Church staff and volunteers with a role that needs it \u2014 the prayer team, the welcome team, the pastor \u2014 can see prayer requests, check-ins and member profiles. SVT Consulting staff may access the server to maintain it. We share information with no one else except as required by law.',
            'Third-party services you open from the app \u2014 Tithe.ly (giving), YouTube (sermons), Facebook, Instagram and Linktree \u2014 are governed by their own privacy policies once you are there.',
          ],
        },
        {
          heading: 'Notifications',
          paragraphs: ['The app does not currently send push notifications. The Notification Settings screen records your preferences for when it does; if that changes, this page will be updated first.'],
        },
        {
          heading: 'Your choices and your rights',
          bullets: [
            'Edit your profile at any time under My Profile.',
            'Delete your account from My Profile \u2192 Delete account. This removes your profile and sign-in; the church may keep prayer requests and check-ins you submitted as part of its own records, without your account attached, unless you ask for them to be removed too.',
            'Ask for a copy of your information, a correction, or deletion of prayer requests and check-ins by emailing the church at ccbnycinfo@gmail.com or support@svtconsulting.co.',
            'Use the app without an account for everything that doesn\u2019t require one.',
          ],
        },
        {
          heading: 'Retention and security',
          paragraphs: ['Account data is kept while your account exists. Prayer requests and check-ins are kept as long as the church needs them for ministry and attendance records. Data travels over HTTPS and is protected by access controls on the server; no method of storage is perfectly secure, and we will notify affected users of a breach as the law requires.'],
        },
        {
          heading: 'Children',
          paragraphs: ['The app is for the general congregation and is not directed at children under 13. Accounts are for people 13 and over; a parent or guardian who believes a child has created an account can email us and we will delete it.'],
        },
        {
          heading: 'Changes to this policy',
          paragraphs: ['If what the app collects or how it is used changes, this page will be updated and the effective date changed, and the app\u2019s release notes will say so.'],
        },
        {
          heading: 'Contact',
          paragraphs: [
            'Cristo Centro de Bendici\u00f3n NYC, 1156 Southern Blvd, Bronx, NY 10459 \u2014 ccbnycinfo@gmail.com.',
            'SVT Consulting LLC (developer and operator of the app on the church\u2019s behalf) \u2014 support@svtconsulting.co.',
          ],
        },
      ],
    },
    support: {
      intro: 'Answers to the common questions. For anything else, email support@svtconsulting.co, or the church at ccbnycinfo@gmail.com.',
      faq: [
        { q: 'Do I need an account?', a: 'No. Announcements, events, sermons, the verse of the day and giving all work without one. An account lets you keep a profile and see your own check-in history.' },
        { q: 'I forgot my password.', a: 'On the sign-in screen tap Forgot password and enter your email; you\u2019ll receive a reset email. If it doesn\u2019t arrive, check your spam folder, then email support@svtconsulting.co.' },
        { q: 'How do I delete my account?', a: 'More \u2192 My Profile \u2192 Delete account. It removes your profile and sign-in straight away. To also remove prayer requests or check-ins you submitted, email the church at ccbnycinfo@gmail.com.' },
        { q: 'Who reads my prayer request?', a: 'The church\u2019s prayer team. Requests are stored on the church\u2019s own server and are not shared outside the church.' },
        { q: 'Does the app take my money for giving?', a: 'No. Give opens the church\u2019s Tithe.ly page in your browser; everything about the payment happens there, under Tithe.ly\u2019s terms. The app never sees card or bank details.' },
        { q: 'A video won\u2019t play.', a: 'Sermons open in YouTube. Make sure you have the YouTube app or a browser available and an internet connection, then try again.' },
        { q: 'Check-in isn\u2019t working.', a: 'Check-in needs an internet connection. Fill in your name and the service you attended, then tap Check In; a confirmation appears when the church\u2019s server has recorded it. If it keeps failing, email support@svtconsulting.co with your phone model and the time you tried.' },
      ],
    },
    terms: {
      effective: 'October 29, 2026',
      sections: [
        { heading: 'What the app is', paragraphs: ['CCB NYC is the official mobile app of Cristo Centro de Bendici\u00f3n NYC, built and maintained by SVT Consulting LLC on the church\u2019s behalf. It is provided free for the congregation and friends of the church.'] },
        { heading: 'Your account', paragraphs: ['You are responsible for keeping your password private and for what is submitted under your account. Give accurate information; you may edit or delete your account at any time.'] },
        { heading: 'Content and conduct', paragraphs: ['Prayer requests, check-in notes and other text you submit are sent to the church. Submit only what you are comfortable sharing with church staff and volunteers, and nothing unlawful, abusive or belonging to someone else. The church may remove content and close accounts that misuse the app.'] },
        { heading: 'Third-party services', paragraphs: ['Giving (Tithe.ly), videos (YouTube) and social links open outside the app and are governed by those services\u2019 own terms and privacy policies.'] },
        { heading: 'No warranty', paragraphs: ['The app is provided \u201cas is\u201d. Service times, events and other content may change; check with the church for anything important. To the fullest extent permitted by law, neither the church nor SVT Consulting LLC is liable for indirect or consequential loss arising from use of the app.'] },
        { heading: 'Changes', paragraphs: ['We may update the app and these terms. Continued use after an update means you accept the updated terms. Questions: support@svtconsulting.co.'] },
      ],
    },
  },
  {
    slug: 'scribbles',
    name: 'Scribbles Learning Center',
    tagline: 'Programs, enrollment and a video introduction for a children\u2019s learning center in Edgewater, NJ.',
    icon: '/images/apps/scribbles.png',
    platforms: ['Web'],
    status: null,
    site: 'https://scribbles-learning-center.vercel.app',
    accent: '#F4A261',
    description:
      'A playful, parent-friendly site with program information, enrollment forms and a video introduction.',
  },
];

export const findApp = (slug) => apps.find((a) => a.slug === slug);
